import { useEffect, useMemo, useState } from "react";
import { formatTitleOwnerNamesFromJson } from "../../helpers/utils";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "../../common/customDesignedComponents/button";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";

const PAGE_SIZE = 10;

function getPageNumbers(
  currentPage: number,
  totalPages: number,
): (number | "ellipsis")[] {
  if (totalPages <= 0) return [];
  const pages = new Set<number>();

  if (currentPage <= 2) {
    // Pages 1–2: show first three and last
    pages.add(1);
    if (totalPages >= 2) pages.add(2);
    if (totalPages >= 3) pages.add(3);
    if (totalPages > 3) pages.add(totalPages);
  } else {
    // Page 3+: always show 1st page; show prev, current, next (e.g. 2, 3, 4), and last
    pages.add(1);
    pages.add(currentPage - 1);
    pages.add(currentPage);
    if (currentPage < totalPages) pages.add(currentPage + 1);
    pages.add(totalPages);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: (number | "ellipsis")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1)
      result.push("ellipsis");
  }
  return result;
}

const SKELETON_ROWS = 5;

const ProeprtyListV2 = ({
  onTimeout,
  selectedPropertyId,
  setSelectedPropertyId,
  getPropertyData,
  getSearchedPropertyData,
}: {
  onTimeout?: () => void;
  selectedPropertyId: number | null;
  setSelectedPropertyId: (id: number | null) => void;
  getPropertyData: any;
  getSearchedPropertyData: (data: any) => void;
}) => {
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);

  const propertyList = useMemo(
    () => (getPropertyData?.success ? (getPropertyData?.data ?? []) : []),
    [getPropertyData],
  );

  const hasData = (propertyList?.length ?? 0) > 0;

  const totalPages = Math.ceil((propertyList?.length ?? 0) / PAGE_SIZE) || 1;
  const paginatedList = useMemo(() => {
    if (!propertyList?.length) return [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return propertyList.slice(start, start + PAGE_SIZE);
  }, [propertyList, currentPage]);

  const pageNumbers = useMemo(
    () => getPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages >= 1) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // If no data after 10 seconds, clear property data and reset (parent sets hasSearched false)
  useEffect(() => {
    if (hasData) return;
    const timeoutId = setTimeout(() => {
      onTimeout?.();
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [hasData, onTimeout]);

  const handleSelectProperty = (data: any, globalIndex: number) => {
    setSelectedPropertyId(globalIndex);
    getSearchedPropertyData(data);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden ml-4 sm:ml-6 md:ml-8"
    >
      <div className="p-6 md:p-8">
        <h2 className="text-xl font-display font-semibold text-foreground text-center mb-2">
          Property List
        </h2>
        <p className="text-secondary text-center text-sm mb-6">
          Please select your relevant property from the property list.
        </p>

        {/* Property Results – skeleton until data, then list */}
        <div className="space-y-0 divide-y divide-border">
          {!hasData
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="flex items-center justify-between py-4 first:pt-0 last:pb-0 animate-pulse"
                >
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                  <div className="h-9 w-24 bg-muted rounded-full ml-4" />
                </div>
              ))
            : paginatedList.map((property: any, indexInPage: number) => {
                const globalIndex = (currentPage - 1) * PAGE_SIZE + indexInPage;
                return (
                  <div
                    key={globalIndex}
                    className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                  >
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-foreground font-medium text-sm">
                          {property.address}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm">
                          {formatTitleOwnerNamesFromJson(property) ||
                            property.owner ||
                            "-"}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        handleSelectProperty(property, globalIndex)
                      }
                      disabled={selectedPropertyId === globalIndex}
                      className={`font-medium px-6 py-2 text-sm rounded-full ml-4 ${
                        selectedPropertyId === globalIndex
                          ? "bg-muted text-muted-foreground cursor-not-allowed"
                          : "bg-secondary hover:bg-secondary/90 text-white"
                      }`}
                    >
                      SELECT
                    </Button>
                  </div>
                );
              })}
        </div>

        {/* Pagination - only when more than 10 properties */}
        {propertyList?.length > PAGE_SIZE && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-9 w-9 rounded-full p-0"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {pageNumbers.map((page, i) =>
              page === "ellipsis" ? (
                <span
                  key={`ellipsis-${i}`}
                  className="px-2 text-muted-foreground"
                >
                  …
                </span>
              ) : (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  onClick={() => setCurrentPage(page)}
                  className={`h-9 min-w-9 rounded-full px-3 text-sm ${
                    currentPage === page
                      ? "bg-secondary hover:bg-secondary/90 text-white"
                      : ""
                  }`}
                >
                  {page}
                </Button>
              ),
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-9 w-9 rounded-full p-0"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProeprtyListV2;
